import { MONTHS } from '@constants/calendar';
import { useEffect, useState } from 'react';
import type {
	ContactFormData,
	PricingEstimationService,
} from '../../../../types';
import { getPersistedFormInput } from '../../../utils/persistedFormInput';
import type { WeddingDateData } from '../../../utils/weddingDate';

interface ProfileData {
	profileGuestCount: string;
	profileWeddingDate: string;
	profileFirstName: string;
	profileLastName: string;
	profileEmail: string;
}

export const useProfilePrefill = ({
	profileGuestCount,
	profileWeddingDate,
	profileFirstName,
	profileLastName,
	profileEmail,
}: ProfileData) => {
	const [persistedFormInput] = useState(getPersistedFormInput);

	const [selectedServices, setSelectedServices] = useState<
		PricingEstimationService[]
	>(() => persistedFormInput?.selectedServices ?? []);
	const [guestCount, setGuestCount] = useState<string>(
		() => persistedFormInput?.guestCount ?? profileGuestCount,
	);
	const [weddingDate, setWeddingDate] = useState<WeddingDateData>(
		() =>
			persistedFormInput?.weddingDate ?? {
				dateOption: '',
				specificDate: '',
				month: '',
				year: '',
			},
	);
	const [contactData, setContactData] = useState<ContactFormData>(() => ({
		firstName: persistedFormInput?.contactData?.firstName ?? profileFirstName,
		lastName: persistedFormInput?.contactData?.lastName ?? profileLastName,
		email: persistedFormInput?.contactData?.email ?? profileEmail,
	}));

	useEffect(() => {
		if (persistedFormInput || !profileGuestCount) return;
		setGuestCount((prev) => prev || profileGuestCount);
	}, [profileGuestCount, persistedFormInput]);

	useEffect(() => {
		if (persistedFormInput?.contactData) return;
		if (profileFirstName || profileLastName || profileEmail) {
			setContactData((prev) => ({
				...prev,
				firstName: prev.firstName || profileFirstName,
				lastName: prev.lastName || profileLastName,
				email: prev.email || profileEmail,
			}));
		}
	}, [profileFirstName, profileLastName, profileEmail, persistedFormInput]);

	useEffect(() => {
		if (persistedFormInput || !profileWeddingDate) return;
		setWeddingDate((prev) => {
			if (prev.dateOption) return prev;
			const match = profileWeddingDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
			if (!match) return prev;
			const monthIndex = Number(match[2]) - 1;
			return {
				dateOption: 'specificDate',
				specificDate: `${match[1]}-${match[2]}-${match[3]}`,
				month: MONTHS[monthIndex] || '',
				year: match[1],
			};
		});
	}, [profileWeddingDate, persistedFormInput]);

	return {
		selectedServices,
		setSelectedServices,
		guestCount,
		setGuestCount,
		weddingDate,
		setWeddingDate,
		contactData,
		setContactData,
	};
};
